import { Fragment, useRef, type MouseEvent } from 'react'
import { Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { mdiAlertCircle, mdiFileUploadOutline, mdiLinkVariant, mdiMinusCircleOutline, mdiPencilOutline, mdiRestore } from '@mdi/js'
import type { ContractChecklistContract, ContractChecklistRow } from '../api/schema'
import { useCan } from '../contexts/PermissionsContext'
import { useFormatters } from '../lib/format'
import { CHECKLIST_STATUS, contractDateItems } from '../lib/contract-checklist'
import { cn } from '../lib/utils'
import { STATUS_PILL_CONFIG } from './AssetCompliance'
import { OverflowMenu, OverflowMenuItem } from './shared/OverflowMenu'
import { AppIcon } from './ui/icon'
import { Button } from './ui/button'

// The pieces the site contract checklist (spec 2026-10-11-17) adds to a
// contract-type group of the asset's Contracts tab: the header decorations,
// the muted lines in the group, and the dates under a contract row.

/** Header controls sit next to the fold toggle; they never fold the section. */
const stop = (e: MouseEvent) => e.stopPropagation()

/** API dates are `YYYY-MM-DD`; format them as that day, whatever the browser's zone. */
function useDay() {
  const { formatDate } = useFormatters()
  return (date: string) => formatDate(date, { timeZone: 'UTC' })
}

export function ChecklistStatusPill({ typeCode, status }: { typeCode: string; status: ContractChecklistRow['status'] }) {
  const { t } = useTranslation('contracts')
  const cfg = CHECKLIST_STATUS[status]
  const pill = STATUS_PILL_CONFIG[cfg.pill]
  return (
    <span
      className={cn('inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium', pill.bg, pill.color)}
      data-testid={`contract-checklist-status-${typeCode}`}
      data-status={status}
    >
      <AppIcon path={pill.icon} size="xs" />
      {t(cfg.labelKey)}
    </span>
  )
}

interface HeaderProps {
  org: string
  assetUid: string
  row: ContractChecklistRow
  factsByUid: Map<string, ContractChecklistContract>
  /** Asset type code → its localized name. */
  assetTypeName: (code: string) => string
  /** Upload a file for this row; resolves once the document is created. */
  onUpload: (file: File) => void
  onWaive: () => void
  onRestore: () => void
  restoring?: boolean
}

/** Status pill, coverage count, « À vérifier » hint, « via » chips and the row's actions. */
export function ChecklistGroupHeader({ org, assetUid, row, factsByUid, assetTypeName, onUpload, onWaive, onRestore, restoring }: HeaderProps) {
  const { t } = useTranslation('contracts')
  const canWriteContracts = useCan('contracts.write')
  const canWriteDocuments = useCan('documents.write')
  const fileInput = useRef<HTMLInputElement>(null)
  const code = row.contract_type.code
  const actionable = row.status === 'missing' || row.status === 'partial'

  const countParts: string[] = []
  if (row.expected > 1) {
    const types = row.expected_asset_type_codes.map(assetTypeName).filter(Boolean).join(', ')
    countParts.push(types
      ? t('checklist.count', { types, covered: row.covered, expected: row.expected })
      : t('checklist.count_no_types', { covered: row.covered, expected: row.expected }))
  }
  if (row.expected > 1 && row.waived > 0) countParts.push(t('checklist.count_waived', { count: row.waived }))

  const generic = row.generic_contract_uids
  const firstGeneric = generic[0] ? factsByUid.get(generic[0]) : undefined

  return (
    // One layout for every row (spec 2026-10-25-20): pill, coverage text
    // (truncated, full text in the tooltip), then the actions — never wrapped
    // above `md`, so the pill does not jump from row to row.
    <div className="flex min-w-0 flex-wrap items-center justify-end gap-2 md:flex-nowrap" onClick={stop}>
      <ChecklistStatusPill typeCode={code} status={row.status} />
      {countParts.length > 0 && (
        <span className="min-w-0 max-w-[26rem] truncate text-xs text-muted-foreground" title={countParts.join(" · ")} data-testid={`contract-checklist-count-${code}`}>
          {countParts.join(' · ')}
        </span>
      )}
      {generic.length > 0 && (
        <Link
          to="/$org/contracts/$contractUid"
          params={{ org, contractUid: generic[0] }}
          className="text-xs text-warning hover:underline"
          data-testid={`contract-checklist-hint-${code}`}
        >
          {t('checklist.generic_hint', { name: firstGeneric?.name ?? '' })}
          {generic.length > 1 && ` ${t('checklist.more', { count: generic.length - 1 })}`}
        </Link>
      )}
      {row.via_contract_uids.map((uid) => {
        const facts = factsByUid.get(uid)
        return (
          <Link
            key={uid}
            to="/$org/contracts/$contractUid"
            params={{ org, contractUid: uid }}
            title={facts?.name}
            className="inline-flex max-w-48 items-center gap-1 truncate rounded-full border border-border px-2 py-0.5 text-xs text-muted-foreground hover:text-foreground"
            data-testid={`contract-checklist-via-${uid}`}
          >
            <AppIcon path={mdiLinkVariant} size="xs" className="shrink-0" />
            <span className="truncate">{t('checklist.via', { name: facts?.held_by?.name ?? facts?.name ?? '' })}</span>
          </Link>
        )
      })}
      {row.status === 'not_applicable' && row.waiver && canWriteContracts && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-8"
          onClick={onRestore}
          disabled={restoring}
          data-testid={`contract-checklist-restore-${code}`}
        >
          <AppIcon path={mdiRestore} size="sm" className="md:mr-1" />
          <span className="hidden md:inline">{t('checklist.restore')}</span>
        </Button>
      )}
      {actionable && canWriteDocuments && (
        <>
          <input
            ref={fileInput}
            type="file"
            accept="application/pdf,image/*"
            className="hidden"
            data-testid={`contract-checklist-upload-input-${code}`}
            onChange={(e) => {
              const file = e.target.files?.[0]
              e.target.value = ''
              if (file) onUpload(file)
            }}
          />
          {/* Outline: the tab's one primary button is « Ajouter un contrat »
              at the top (spec 2026-10-25-20). */}
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-8 shrink-0"
            onClick={() => fileInput.current?.click()}
            aria-label={t('checklist.add')}
            title={t('checklist.add')}
            data-testid={`contract-checklist-add-${code}`}
          >
            <AppIcon path={mdiFileUploadOutline} size="sm" className="md:mr-1" />
            <span className="hidden md:inline">{t('checklist.add')}</span>
          </Button>
        </>
      )}
      {actionable && (
        <OverflowMenu size="sm" hidden={!canWriteContracts} testId={`contract-checklist-menu-${code}`}>
          <OverflowMenuItem icon={mdiPencilOutline} label={t('checklist.create')} testId={`contract-checklist-create-${code}`}>
            <Link to="/$org/contracts/create" params={{ org }} search={{ asset_uid: assetUid, contract_type_uid: row.contract_type.uid }} />
          </OverflowMenuItem>
          <OverflowMenuItem icon={mdiMinusCircleOutline} label={t('checklist.waive')} onSelect={onWaive} testId={`contract-checklist-waive-${code}`} />
        </OverflowMenu>
      )}
    </div>
  )
}

/** The muted lines inside a group: not covered assets, the waiver reason, the upload confirmation, an empty body. */
export function ChecklistGroupNotes({ org, row, empty, uploaded }: {
  org: string
  row: ContractChecklistRow
  empty: boolean
  uploaded: boolean
}) {
  const { t } = useTranslation('contracts')
  const day = useDay()
  const { formatDate } = useFormatters()
  const code = row.contract_type.code
  const lines = []
  if (uploaded) {
    lines.push(
      <p key="uploaded" className="text-xs text-success" role="status" data-testid={`contract-checklist-uploaded-${code}`}>
        {t('checklist.uploaded')}
      </p>,
    )
  }
  if (row.status === 'not_applicable' && row.waiver) {
    lines.push(
      <p key="reason" className="break-words text-xs text-muted-foreground" data-testid={`contract-checklist-reason-${code}`}>
        {t('checklist.waiver_reason', { asset: row.waiver.asset_name, reason: row.waiver.reason })}
      </p>,
    )
  }
  if (row.status === 'partial' && row.uncovered_assets.length > 0) {
    lines.push(
      <p key="uncovered" className="break-words text-xs text-muted-foreground" data-testid={`contract-checklist-uncovered-${code}`}>
        {t('checklist.uncovered')}{' '}
        {row.uncovered_assets.map((a, i) => (
          <span key={a.uid}>
            {i > 0 && ', '}
            <Link to="/$org/assets/$assetUid" params={{ org, assetUid: a.uid }} className="hover:text-foreground hover:underline">
              {a.name}
            </Link>
          </span>
        ))}
      </p>,
    )
  }
  if (row.follow_up) {
    const fu = row.follow_up
    lines.push(
      <p key="follow-up" className="break-words text-xs text-muted-foreground" data-testid={`contract-checklist-row-follow-up-${code}`}>
        <Link to="/$org/issues/$uid" params={{ org, uid: fu.issue_uid }} className="hover:text-foreground hover:underline">
          {t('checklist.row_follow_up', { title: fu.title })}
        </Link>
        {fu.quotes_requested_at && t('checklist.row_follow_up_quotes', {
          date: formatDate(fu.quotes_requested_at),
          count: fu.quotes_sent_to,
        })}
      </p>,
    )
  }
  if (empty && row.status !== 'not_applicable') {
    lines.push(
      <p key="empty" className="text-xs text-muted-foreground" data-testid={`contract-checklist-empty-${code}`}>
        {row.lapsed ? t('checklist.dates.lapsed', { date: day(row.lapsed.ended_on) }) : t('checklist.empty_group')}
      </p>,
    )
  }
  if (lines.length === 0) return null
  return <div className={cn('space-y-1 px-4 py-2', row.status === 'not_applicable' && 'opacity-80')}>{lines}</div>
}

/** The dates under a contract row, and its follow-up ticket when a contract rule opened one. */
export function ContractRowDates({ org, facts, typeCode }: { org: string; facts: ContractChecklistContract; typeCode?: string }) {
  const { t } = useTranslation('contracts')
  const day = useDay()
  const today = new Date().toLocaleDateString('sv-SE')
  const items = contractDateItems(facts, typeCode ?? facts.contract_type_code, today)
  const unknown = STATUS_PILL_CONFIG.UNKNOWN
  return (
    <>
      {facts.follow_up && (
        <Link
          to="/$org/issues/$uid"
          params={{ org, uid: facts.follow_up.issue_uid }}
          className="mt-1 flex items-start gap-1 break-words text-xs font-medium text-destructive hover:underline"
          aria-label={t('checklist.follow_up_label', { title: facts.follow_up.title })}
          data-testid={`contract-follow-up-${facts.uid}`}
        >
          <AppIcon path={mdiAlertCircle} size="xs" className="mt-0.5 shrink-0" />
          <span>
            {facts.follow_up.title}
            {facts.follow_up.count > 1 && ` ${t('checklist.more', { count: facts.follow_up.count - 1 })}`}
          </span>
        </Link>
      )}
      {items.length > 0 && (
        <div className="mt-0.5 text-xs leading-5 text-muted-foreground" data-testid={`contract-dates-${facts.uid}`}>
          {items.map((item, i) => (
            <Fragment key={item.kind}>
              {i > 0 && ' '}
              <span className="whitespace-nowrap">
              {item.kind === 'cadence_unknown' ? (
                <span
                  className={cn('inline-block rounded border border-dashed border-muted-foreground/40 px-1', unknown.bg, unknown.color)}
                  data-testid={`contract-cadence-unknown-${facts.uid}`}
                >
                  {t('checklist.dates.cadence_unknown')}
                </span>
              ) : (
                <span className={cn(item.kind === 'lapsed' && 'text-destructive')}>
                  {t(`checklist.dates.${item.kind}`, { date: day(item.date) })}
                </span>
              )}
              {i < items.length - 1 && <span aria-hidden>{' ·'}</span>}
              </span>
            </Fragment>
          ))}
        </div>
      )}
    </>
  )
}
